/**
 * useCampaignReviewDeepLink — resolves a script/content-review deep link
 * (`?panel=script-review|content-review&creator=<username>&thread=open`)
 * against a tab's loaded entries.
 *
 * Unlike useCampaignDeepLinkPanel (which drives the global Zustand store for
 * insights / brand-safety / conversation drawers), the script/content review
 * panels open via LOCAL component state — ScriptsTab/ContentTab's
 * `dialogCreatorId` + the review panel's `threadOpen`. So this hook is a pure
 * read-only resolver: it returns *what* the tab should open and the tab applies
 * it to its own state.
 *
 * One-way (URL -> caller): it never writes the URL back. Outbound URL tracking
 * (writing the panel/creator/thread params as the user clicks around) is a
 * separate follow-up.
 *
 * `creator` is matched by platform **username** (same convention as the rest of
 * the campaign deep links — readable + shareable). If the username is not on
 * the tab's loaded entries we return `creatorId: null` (a no-op for the caller).
 * We do NOT touch the URL — the link stays intact for a future by-username
 * resolver targeting an off-page creator.
 *
 * Status guard: a `panel=script-review` URL must NOT open the review panel
 * for a creator whose post is in `script_pending` (the script hasn't been
 * uploaded yet — there's nothing to review). Same for `content-review` /
 * `content_pending`. The reader checks the row's status and bails when the
 * panel doesn't apply. The Phase E redirect in useCampaignDeepLinkPanel is
 * responsible for routing the user to the correct pill in that case.
 */
import { useSearch } from '@tanstack/react-router';
import type { CampaignSearch } from './search';

type ReviewEntry = { id: string; platform?: { username?: string }; status?: string };
type ReviewKind = 'script-review' | 'content-review';

const EXPECTED_STATUS: Record<ReviewKind, string> = {
  'script-review': 'script_review',
  'content-review': 'content_review',
};

export function useCampaignReviewDeepLink(
  entries: ReviewEntry[],
  kind: ReviewKind,
): { creatorId: string | null; threadOpen: boolean } {
  const { panel, creator, thread } = useSearch({ strict: false }) as Partial<CampaignSearch>;
  if (panel !== kind || !creator) return { creatorId: null, threadOpen: false };

  const row = entries.find((e) => e.platform?.username === creator);
  if (!row) return { creatorId: null, threadOpen: false };

  // Status guard — refuse to open a *_review panel for a *_pending row.
  if (row.status !== undefined && row.status !== EXPECTED_STATUS[kind]) {
    return { creatorId: null, threadOpen: false };
  }

  return { creatorId: row.id, threadOpen: thread === 'open' };
}
